<template>
  <section class="p-6 flex flex-col gap-6">
    <nav class="text-gray-600 text-sm">
      <span class="font-semibold cursor-pointer" @click="router.push('/')">Home</span>
      <span class="mx-2">/</span>
      <span class="cursor-pointer font-semibold" @click="router.push({ name: 'Services' })">Services</span>
      <span class="mx-2">/</span>
      <span class="text-gray-800 font-bold">Edit Service</span>
    </nav>

    <h3 class="font-bold text-2xl">Edit Service #{{ id }}</h3>

    <div v-if="loading" class="text-gray-500 text-sm">Loading service...</div>

    <form
      v-else
      @submit.prevent="submitForm"
      class="bg-white shadow rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Name (Arabic)</label>
          <input v-model="form.name_ar" class="border w-full rounded-md px-3 py-2" />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Name (English)</label>
          <input v-model="form.name_en" class="border w-full rounded-md px-3 py-2" />
        </div>

        <div>
          <label class="block text-sm font-medium mb-1">Category</label>
          <select v-model="form.category" class="border w-full rounded-md px-3 py-2">
            <option value="translation">Translation</option>
            <option value="academic">Academic</option>
            <option value="religious">Religious</option>
          </select>
        </div>
      </div>

      <!-- Description (AR/EN) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Full Description (Arabic)</label>
          <textarea v-model="form.description_ar" rows="4" class="border w-full rounded-md px-3 py-2"></textarea>
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Full Description (English)</label>
          <textarea v-model="form.description_en" rows="4" class="border w-full rounded-md px-3 py-2"></textarea>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Price Text</label>
          <input v-model="form.price_text" class="border w-full rounded-md px-3 py-2" />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Sort Order</label>
          <input v-model.number="form.sort_order" type="number" class="border w-full rounded-md px-3 py-2" />
        </div>
      </div>

      <!-- Icon -->
      <div>
        <label class="block text-sm font-medium mb-1">Icon (optional)</label>
        <input type="file" @change="onFileChange" accept="image/*" />

        <!-- preview -->
        <img
          v-if="preview"
          :src="preview"
          class="mt-2 w-16 h-16 object-contain rounded shadow"
        />

        <!-- current icon -->
        <img
          v-else-if="form.icon_url"
          :src="form.icon_url"
          class="mt-2 w-16 h-16 object-contain rounded shadow"
          alt="Current icon"
        />

        <div v-else class="mt-2 text-xs text-gray-500">No icon</div>
      </div>

      <!-- Active -->
      <div>
        <label class="flex items-center gap-2">
          <input type="checkbox" v-model="form.is_active" />
          <span>Active</span>
        </label>
      </div>

      <button
        type="submit"
        class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 self-end disabled:opacity-60"
        :disabled="saving"
      >
        <span v-if="saving">Saving...</span>
        <span v-else>Save Changes</span>
      </button>
    </form>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient";

const route = useRoute();
const router = useRouter();
const id = route.params.id;

const loading = ref(false);
const saving = ref(false);

const form = ref({
  name_ar: "",
  name_en: "",
  category: "translation",
  description_ar: "",
  description_en: "",
  price_text: "",
  sort_order: 0,
  is_active: true,
  icon_url: "",
});

const preview = ref(null);
const iconFile = ref(null);

async function fetchService() {
  loading.value = true;
  try {
    const res = await apiClient.get(`/admin/services/${id}`);
    const service = res.data?.data ?? {};

    // ✅ نفس شكل add
    form.value = {
      name_ar: service.name_ar ?? "",
      name_en: service.name_en ?? "",
      category: service.category ?? "translation",
      description_ar: service.description_ar ?? service.description_ar ?? "",
      description_en: service.description_en ?? service.description_en ?? "",
      price_text: service.price_text ?? "",
      sort_order: service.sort_order ?? 0,
      is_active: !!service.is_active,
      icon_url: service.icon_url ?? "",
    };
  } catch (error) {
    alert("Failed to load service.");
  } finally {
    loading.value = false;
  }
}

function onFileChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  iconFile.value = file;
  preview.value = URL.createObjectURL(file);
}

async function submitForm() {
  saving.value = true;

  const fd = new FormData();
  fd.append("name_ar", form.value.name_ar);
  fd.append("name_en", form.value.name_en);
  fd.append("category", form.value.category);
  fd.append("description_ar", form.value.description_ar);
  fd.append("description_en", form.value.description_en);
  fd.append("price_text", form.value.price_text);
  fd.append("sort_order", form.value.sort_order ?? 0);
  fd.append("is_active", form.value.is_active ? "1" : "0");

  if (iconFile.value) fd.append("icon", iconFile.value);

  try {
    await apiClient.post(`/admin/services/${id}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    alert("Service updated successfully!");
    router.push({ name: "Services" });
  } catch (err) {
    console.error(err);
    alert("Failed to update service.");
  } finally {
    saving.value = false;
  }
}

onMounted(fetchService);
</script>
